import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCCheckbox } from 'rollup-allspark';
import ComponentPlaceholder from '../../../../components/ComponentPlaceholder';
import { cn, isEmpty, isString } from '../../../../utils';
import { updateEntity } from '../../actions';
import { makeSelectProperty } from '../../selector';

const ItemList = ({
  checkedCategories = [],
  checkedItems = [],
  items = [],
  itemSearchKey = '',
  selectedFilterCategory = null,
  selectedFilterMenu = null,
  updateField,
}) => {
  const [filteredItems, setFilteredItems] = useState(items || []);

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    if (
      !isEmpty(selectedFilterCategory) ||
      !isEmpty(selectedFilterMenu) ||
      !isEmpty(itemSearchKey)
    ) {
      const filtered = items.filter(i =>
        i.linkedCategories?.some(lc => {
          const matchesCategory = !isEmpty(selectedFilterCategory)
            ? lc.categoryID === selectedFilterCategory.id
            : true;

          const matchesMenu = !isEmpty(selectedFilterMenu)
            ? lc.linkedSections.some(
                ls => ls.id === selectedFilterMenu.sectionID,
              )
            : true;

          const searchKey =
            !isEmpty(itemSearchKey) && isString(itemSearchKey)
              ? itemSearchKey.toLowerCase().trim()
              : '';

          const matchesSearchKey =
            i.title.en.toLowerCase().includes(searchKey) ||
            `${i.id}` === searchKey;

          return matchesCategory && matchesMenu && matchesSearchKey;
        }),
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [items, itemSearchKey, selectedFilterCategory, selectedFilterMenu]);

  const onItemSelected = (i, v, isDisabled) => {
    if (isDisabled) return;

    const filedsToUpdate = {};

    if (v) {
      filedsToUpdate.checkedItems = [...new Set([...checkedItems, i.id])];
    } else {
      filedsToUpdate.checkedItems = checkedItems.filter(id => id !== i.id);
    }

    updateField(filedsToUpdate);
  };

  if (isEmpty(filteredItems))
    return (
      <div className="w-full flex flex-col items-center justify-center">
        <ComponentPlaceholder label="No items found" />
      </div>
    );

  const listEl = filteredItems.map(i => {
    const selectedFromCategory = i.linkedCategories.find(lc =>
      checkedCategories.includes(lc.categoryID),
    );

    const isDisabled = !isEmpty(selectedFromCategory);

    const selectedEl = isDisabled ? (
      <div className="w-1/3 flex justify-end">
        <div className="px-2 py-1 rounded-full bg-primary-lightest border-primary border text-primary text-mini">
          Selected from &nbsp;
          <span className="font-medium">{selectedFromCategory.title.en}</span>
        </div>
      </div>
    ) : (
      <div className="w-1/3" />
    );

    return (
      <div
        key={i.id}
        className="flex justify-between px-4 py-3 cursor-pointer"
        role="presentation"
        onClick={() =>
          onItemSelected(i, !checkedItems.includes(i.id), isDisabled)
        }
      >
        <div className="flex gap-x-2 items-center w-2/3">
          <SCCheckbox
            disabled={isDisabled}
            value={checkedItems.includes(i.id) || selectedFromCategory}
            handleChange={v =>
              onItemSelected(i, v, !isEmpty(selectedFromCategory))
            }
          />
          <div
            className={cn({
              'flex items-center justify-center cursor-pointer': true,
              'text-grey-darker': isDisabled,
            })}
          >
            {i.id}. {i.title.en}
          </div>
        </div>
        {selectedEl}
      </div>
    );
  });

  const headerEl = (
    <div className="flex items-center bg-grey-lighter py-3 px-4 font-medium rounded-t-lg">
      Items
    </div>
  );

  return (
    <div className="flex flex-col divide-y divide-grey w-full rounded-lg border border-grey">
      {headerEl}
      {listEl}
    </div>
  );
};

ItemList.propTypes = {
  checkedCategories: PropTypes.array,
  checkedItems: PropTypes.array,
  items: PropTypes.array,
  itemSearchKey: PropTypes.string,
  selectedFilterCategory: PropTypes.object,
  selectedFilterMenu: PropTypes.object,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  checkedCategories: makeSelectProperty('checkedCategories'),
  checkedItems: makeSelectProperty('checkedItems'),
  items: makeSelectProperty('items'),
  itemSearchKey: makeSelectProperty('itemSearchKey'),
  selectedFilterCategory: makeSelectProperty('selectedFilterCategory'),
  selectedFilterMenu: makeSelectProperty('selectedFilterMenu'),
});

const mapDispatchToProps = dispatch => ({
  updateField: payload => dispatch(updateEntity(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(ItemList);
